import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { LiquidityPosition, PartnerLiquidity } from "@greenbook/client";
import {
  isOrderSheetError,
  OrderSheetError,
  userMessageFor,
  type OrderSheetClient,
  type PositionRef,
  type Sheet,
  type SheetInit,
  type StakeUnit,
} from "@openmarketsai/order-sheet";
import { useOrderSheet } from "@openmarketsai/order-sheet/react";
import { invalidateAfterOrder, useLiquidity } from "../lib/queries.js";
import { useOddsFormat } from "../lib/odds.js";
import { fmtPrice } from "../lib/format.js";
import { ApiError, entitlementOf } from "../lib/api.js";
import { showUnlock, UnlockCard } from "./Gate.js";
import { useIsMobile } from "../lib/responsive.js";
import { VenueLogo } from "./VenueLogo.js";
import { SignInButton } from "./SignInWithOpenMarkets.js";
import { onAuthRejected, resolveApiKey, SCOPES, useCanTrade, useIsAuthed } from "../lib/omauth.js";
import { setTradingMode, useTradingMode, useVenueStatus } from "../lib/venues.js";
import { useOrderSettings } from "../lib/settings.js";
import { trackOpenSheet, useOrderClient } from "../lib/orderClient.js";
import { flyToPositions } from "../lib/flyToNav.js";
import {
  centsText,
  deriveTicketView,
  gateProblems,
  LOOKUP_SCHEDULE_MS,
  matchSentOrder,
  parseCents,
  parseStake,
  readStake,
  snapshotSend,
  writeStake,
  type FixAction,
  type LookupState,
  type Mode,
  type OrderRowLike,
  type ProblemVM,
  type SendSnapshot,
  type TicketInput,
} from "../lib/ticketModel.js";
import { phaseFromSheet } from "../lib/orderFlow.js";
import { TicketView, type TicketActions } from "./ticket/TicketView.js";
import { marketHref } from "../lib/marketView.js";

type TicketProps = {
  position: LiquidityPosition;
  pl: PartnerLiquidity;
  /**
   * Kept for ContestDetail's contract. The ticket's mode FOLLOWS greenbook's global trading
   * mode (it is what arms the order SDK for real money), so this is informational only.
   */
  initialMode?: Mode;
  /**
   * Prefill the stake (in dollars / ATLAS, the "amount" unit) instead of the remembered one —
   * Portfolio's Hedge opens the ticket with the stake that locks in about the same payout.
   * Only the input's starting value; the SDK still quotes and validates it like a typed stake.
   */
  initialStake?: number;
  /** Prefill the max price (0–1), e.g. the order-book level tapped on the Market screen. */
  initialMaxPrice?: number;
  /** Show "View market" (off on the Market screen itself). */
  marketLink?: boolean;
  /**
   * Place into this existing opportunity (order-sheet ≥0.1.2 `SheetInit.opportunityId`), so a
   * hedge groups with the order it hedges. Must be a server-issued id.
   */
  opportunityId?: string;
  /**
   * File the order under this model (`SheetInit.modelId`). With `opportunityId`, pass the
   * opportunity's own model when it has one: the API refuses a different (or missing) model on an
   * opportunity that has one (`400 model_mismatch`). Leave it out when the opportunity has none.
   */
  modelId?: string;
  onClose: () => void;
};

/** Where a user connects or funds a venue (greenbook doesn't manage venue accounts). */
const VENUE_ACCOUNTS_URL = "https://app.openmarkets.ai";
/** Problems wait this long after the last keystroke (spec §2 "Timing"). */
const TYPING_PAUSE_MS = 400;
/** A quiet re-price at most this often (stale quote / the page's price moved). */
const QUIET_REFRESH_MIN_MS = 10_000;
/** "Sending" → "At <venue>" in the overlay. */
const AT_VENUE_STEP_MS = 450;
/** A placement still in flight after this long is treated as an unknown outcome and looked up. */
const PLACE_WATCHDOG_MS = 30_000;

/**
 * The ticket entry point. Signed out there is no order form at all — just the picked outcome
 * and "Sign in to trade". Signed in, the order SDK's sheet below.
 */
export function TradeTicket(props: TicketProps) {
  const authed = useIsAuthed();
  if (!authed) return <SignedOutTicket {...props} />;
  return <TradeTicketForm {...props} />;
}

function SignedOutTicket({
  position,
  pl,
  onClose,
  message = "Sign in with your OpenMarkets account to trade — paper trading works on a free plan.",
}: TicketProps & { message?: string }) {
  const { format } = useOddsFormat();
  const isMobile = useIsMobile();
  const venueName = pl.partner_name ?? pl.partner_id;
  return (
    <aside className="flex h-full w-full flex-1 flex-col bg-panel">
      {!isMobile && (
        <div className="flex items-center gap-2 border-b border-border bg-panel-2 px-3 py-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-amber">Trade ticket</h2>
          <button onClick={onClose} className="ml-auto text-dim hover:text-ink" title="Close ticket" aria-label="Close ticket">
            ✕
          </button>
        </div>
      )}
      <div className={`space-y-3 ${isMobile ? "p-4" : "p-3"}`}>
        <div className="rounded-lg border border-border bg-panel-2 p-3">
          <div className="break-words text-sm font-semibold text-ink">{position.title ?? "(position)"}</div>
          <div className="mt-2.5 flex items-center gap-2.5">
            <VenueLogo name={venueName} size={22} />
            <span className="text-[13px] text-ink">{venueName}</span>
            {pl.price != null && <span className="nums ml-auto text-lg font-semibold text-ink">{fmtPrice(pl.price, pl.american_odds, format)}</span>}
          </div>
        </div>
        <p className="text-[12px] leading-relaxed text-dim">{message}</p>
        <SignInButton label="Sign in to trade" size="lg" feedback />
      </div>
    </aside>
  );
}

/**
 * Signed in: one order SDK client app-wide (lib/orderClient.ts). No client = the legacy pasted
 * key, which has no SDK session to hand the order SDK — the ticket asks for a real sign-in.
 * Each picked cell gets a fresh sheet (keyed remount), anchored to the clicked venue.
 */
function TradeTicketForm(props: TicketProps) {
  const om = useOrderClient();
  if (!om) {
    return (
      <SignedOutTicket
        {...props}
        message="Placing orders needs a Sign in with OpenMarkets session (a pasted API key can't place orders through the order SDK)."
      />
    );
  }
  return <SheetTicket key={`${props.position.position_hash}|${props.pl.liquidity_hash ?? props.pl.partner_id}`} om={om} {...props} />;
}

/** greenbook's mode, as the order SDK sees it: Real only when armed (global Real + trade scope). */
function useArmedMode(): Mode {
  const mode = useTradingMode();
  const canTrade = useCanTrade();
  return mode === "real" && canTrade ? "real" : "paper";
}

function toSheetError(e: unknown): OrderSheetError {
  return isOrderSheetError(e) ? e : new OrderSheetError({ code: "network_error", message: e instanceof Error ? e.message : String(e), retryable: true });
}

/**
 * The live container: opens the SDK sheet (headless — no SDK components), keeps the few
 * pieces of state the SDK doesn't (stake text, the send in flight, the unknown-outcome lookup,
 * the debounced problems), derives the view-model (lib/ticketModel.ts) and renders
 * components/ticket/TicketView.tsx.
 *
 * Safety is the SDK's and orderClient.ts's, unchanged: real money only when the global mode
 * is Real and the session has trade:execute, and the SDK's own confirm step before every real
 * order. Nothing here places an order except an explicit tap.
 *
 * Exported for the dev sandbox (/dev/ticket), which runs it against an in-memory fake API.
 */
export function SheetTicket({ om, position, pl, initialStake, initialMaxPrice, marketLink = true, opportunityId, modelId, onClose }: TicketProps & { om: OrderSheetClient }) {
  const { format } = useOddsFormat();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const mode = useArmedMode();
  const canTrade = useCanTrade();
  const settings = useOrderSettings();
  const venueStatus = useVenueStatus();
  const contestId = position.contest_id ?? null;
  const liveQ = useLiquidity(contestId ?? undefined);

  const ref = useMemo<PositionRef>(
    () =>
      pl.liquidity_hash
        ? { liquidityHash: pl.liquidity_hash }
        : { positionHash: position.position_hash, partnerId: pl.partner_id },
    [pl.liquidity_hash, position.position_hash, pl.partner_id],
  );

  // ── greenbook-side state ──
  const prefill = initialStake != null && Number.isFinite(initialStake) && initialStake > 0 ? initialStake : null;
  const [remembered] = useState(() => {
    const r = readStake(mode);
    return prefill == null ? r : { unit: "amount" as StakeUnit, text: { ...r.text, amount: String(prefill) } };
  });
  const [unit, setUnit] = useState<StakeUnit>(remembered.unit);
  const [stakeText, setStakeText] = useState<Record<StakeUnit, string>>(remembered.text);
  // cents; null = the SDK's auto ceiling
  const [priceText, setPriceText] = useState<string | null>(() =>
    initialMaxPrice != null && initialMaxPrice > 0 && initialMaxPrice < 1 ? centsText(initialMaxPrice) : null,
  );
  const [verifyLiquidity, setVerifyLiquidity] = useState(false);
  const [placeError, setPlaceError] = useState<OrderSheetError | null>(null);
  const [send, setSend] = useState<SendSnapshot | null>(null);
  const [sendStep, setSendStep] = useState<0 | 1>(0);
  const [lookup, setLookup] = useState<LookupState | null>(null);
  const [cancel, setCancel] = useState<{ busy: boolean; note: string | null }>({ busy: false, note: null });
  const [, setRenderTick] = useState(0);
  // A prefilled stake counts as typed, so a mode flip doesn't swap it for the remembered one.
  const edited = useRef(prefill != null);
  const lastEditAt = useRef(0);
  const shownProblems = useRef<ProblemVM[]>([]);
  const selectedRef = useRef<string>(pl.partner_id);
  const seenOrderIds = useRef(new Set<string>());
  const loadedSheets = useRef(new WeakSet<Sheet>());
  const lookupRun = useRef(0);

  // Read when a sheet OPENS (first time, and again when the client is rebuilt because the
  // armed state flipped), so a mode change keeps the user's venue, stake and price.
  const stakeNum = parseStake(stakeText[unit]);
  const priceNum = priceText == null ? null : parseCents(priceText);
  const init: SheetInit = {
    mode,
    orderType: "market",
    partnerId: selectedRef.current,
    ...(stakeNum != null ? { stake: unit === "amount" ? { amount: stakeNum } : { shares: stakeNum } } : {}),
    ...(priceNum != null ? { maxPrice: priceNum } : {}),
    ...(opportunityId ? { opportunityId } : {}),
    ...(modelId ? { modelId } : {}),
  };
  const h = useOrderSheet(om, ref, init);
  const sheet = h.sheet;
  if (!h.loading) loadedSheets.current.add(sheet);
  const everLoaded = loadedSheets.current.has(sheet);

  useEffect(() => trackOpenSheet(), []);
  useEffect(() => {
    if (h.venue) selectedRef.current = h.venue.partnerId;
  }, [h.venue]);

  // A new sheet (the client was rebuilt on a mode flip) starts with nothing in flight.
  useEffect(() => {
    setSend(null);
    setLookup(null);
    setPlaceError(null);
    setCancel({ busy: false, note: null });
  }, [sheet]);

  // Stake memory is per mode: a mode flip before the user typed anything loads that mode's.
  useEffect(() => {
    if (edited.current) return;
    const r = readStake(mode);
    setUnit(r.unit);
    setStakeText(r.text);
  }, [mode]);

  // The sheet's mode follows greenbook's global switch.
  useEffect(() => {
    if (!h.loading && h.mode !== mode) sheet.set({ mode });
  }, [mode, h.mode, h.loading, sheet]);

  // Limit orders: greenbook's setting AND the venue's served order types.
  const canLimit = settings.enable_limit_orders && !!h.venue?.supports.orderTypes.includes("limit");
  useEffect(() => {
    if (!canLimit && h.orderType === "limit") sheet.set({ orderType: "market" });
  }, [canLimit, h.orderType, sheet]);

  // The rest of the app refreshes on every step of the order's trip.
  useEffect(() => {
    if (h.result) invalidateAfterOrder(qc);
  }, [h.result, h.phase, qc]);

  // A dead credential behaves as it does everywhere else in greenbook.
  const err = placeError ?? h.error;
  useEffect(() => {
    if (!err) return;
    if (err.status === 401) onAuthRejected();
    else if (err.code === "no_credential") void resolveApiKey();
  }, [err]);

  // ── typing pause (problems wait; the button doesn't) ──
  const markEdit = () => {
    edited.current = true;
    lastEditAt.current = Date.now();
    setRenderTick((n) => n + 1);
    window.setTimeout(() => setRenderTick((n) => n + 1), TYPING_PAUSE_MS + 20);
  };

  const pushStake = (u: StakeUnit, text: string) => {
    setPlaceError(null);
    const n = parseStake(text);
    sheet.set({ stake: n == null ? null : u === "amount" ? { amount: n } : { shares: n } });
  };

  // ── quiet re-pricing (spec §5 "Stale price") ──
  const lastLoadAt = useRef(Date.now());
  const lastQuietRefresh = useRef(0);
  useEffect(() => {
    if (!h.loading) lastLoadAt.current = Date.now();
  }, [h.loading, sheet]);
  const quietRefresh = useCallback(
    (force = false) => {
      const st = sheet.getState();
      if (st.loading) return;
      if (!force && (send || !["ready", "blocked", "idle"].includes(st.phase))) return;
      if (Date.now() - lastQuietRefresh.current < QUIET_REFRESH_MIN_MS) return;
      lastQuietRefresh.current = Date.now();
      void sheet.refresh();
    },
    [sheet, send],
  );
  useEffect(() => {
    if (h.quote?.freshness === "stale") quietRefresh();
  }, [h.quote?.freshness, quietRefresh]);
  useEffect(() => {
    const staleAfter = om.options.defaults.staleAfterMs;
    const id = window.setInterval(() => {
      if (Date.now() - lastLoadAt.current > staleAfter) quietRefresh();
    }, 5_000);
    return () => window.clearInterval(id);
  }, [om, quietRefresh]);
  // The venue's price moved on the page (our live / polled liquidity).
  const pagePrice = useMemo(() => {
    const id = h.venue?.partnerId ?? pl.partner_id;
    const pos = liveQ.data?.find((p) => p.position_hash === position.position_hash);
    return pos?.partner_liquidities?.find((x) => x.partner_id === id)?.price ?? null;
  }, [liveQ.data, position.position_hash, h.venue?.partnerId, pl.partner_id]);
  const sheetPriceRef = useRef<number | null>(null);
  sheetPriceRef.current = h.venue?.price ?? null;
  useEffect(() => {
    const now = sheetPriceRef.current;
    if (pagePrice != null && now != null && Math.abs(pagePrice - now) > 0.0005) quietRefresh();
  }, [pagePrice, quietRefresh]);

  // ── the send ──
  useEffect(() => {
    if (!send || lookup) return;
    setSendStep(0);
    const t = window.setTimeout(() => setSendStep(1), AT_VENUE_STEP_MS);
    return () => window.clearTimeout(t);
  }, [send, lookup]);

  // A declined order whose leg carries no reason (e.g. accepted, then closed by the venue): the
  // reason is only on the order row (`failure_reason`), so read it once from the blotter.
  const [declineReason, setDeclineReason] = useState<string | null>(null);
  const trackedIds = (h.result?.legs ?? []).flatMap((l) => l.routerOrderIds).join(",");
  const legExplains = (h.result?.legs ?? []).some((l) => !!l.issue?.message);
  const declined = phaseFromSheet(h.phase, h.result, null, h.orders) === "failed";
  useEffect(() => {
    setDeclineReason(null);
    if (!declined || legExplains || !trackedIds) return;
    let alive = true;
    const ids = new Set(trackedIds.split(","));
    om.listOrders({ contestId: contestId ?? undefined, limit: 50 })
      .then((rows) => {
        const row = (rows as OrderRowLike[]).find((r) => ids.has(r.router_order_id) && !!r.failure_reason);
        if (alive && row?.failure_reason) setDeclineReason(String(row.failure_reason));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [declined, legExplains, trackedIds, om, contestId]);

  const startLookup = useCallback(
    (snap: SendSnapshot) => {
      const run = ++lookupRun.current;
      setLookup({ status: "checking", attempts: 0 });
      const attempt = async (i: number) => {
        if (run !== lookupRun.current) return;
        try {
          const rows = (await om.listOrders({ contestId: snap.contestId ?? undefined, limit: 50 })) as OrderRowLike[];
          if (run !== lookupRun.current) return;
          const m = matchSentOrder(rows, snap, { now: Date.now(), exclude: seenOrderIds.current });
          if (m.length === 1) {
            seenOrderIds.current.add(m[0].router_order_id);
            setLookup({ status: "found", row: m[0] });
            invalidateAfterOrder(qc);
            return;
          }
          if (m.length > 1) {
            setLookup({
              status: "unresolved",
              note: `We found ${m.length} recent orders that look like this one, so we can't tell which is yours. Check Portfolio before placing it again.`,
            });
            return;
          }
        } catch {
          /* try again on the next beat */
        }
        if (run !== lookupRun.current) return;
        if (i + 1 < LOOKUP_SCHEDULE_MS.length) {
          setLookup({ status: "checking", attempts: i + 1 });
          window.setTimeout(() => void attempt(i + 1), LOOKUP_SCHEDULE_MS[i + 1] - LOOKUP_SCHEDULE_MS[i]);
        } else {
          setLookup({
            status: "unresolved",
            note: "It isn't in your recent orders yet. It may still show up — check Orders before placing it again.",
          });
        }
      };
      window.setTimeout(() => void attempt(0), LOOKUP_SCHEDULE_MS[0]);
    },
    [om, qc],
  );
  useEffect(() => () => void ++lookupRun.current, []);

  // A placement that hangs (the SDK sets no request timeout) becomes an unknown outcome.
  useEffect(() => {
    if (!send || lookup || h.phase !== "placing") return;
    const t = window.setTimeout(() => startLookup(send), PLACE_WATCHDOG_MS);
    return () => window.clearTimeout(t);
  }, [send, lookup, h.phase, startLookup]);

  /**
   * Place, or (`viaConfirm`) the REAL MONEY card's Confirm. A paper order sends on the first
   * call; a real one returns `confirm_required` first and sends on the confirm.
   */
  const placeNow = async (viaConfirm: boolean) => {
    setPlaceError(null);
    const st = sheet.getState();
    if (!viaConfirm && !st.canPlace) return;
    const sends = viaConfirm || st.mode === "paper" || !om.options.realMoney.confirm;
    let snap: SendSnapshot | null = null;
    if (sends) {
      snap = snapshotSend(st, contestId, position.title ?? null, Date.now());
      if (snap) {
        setSend(snap);
        setLookup(null);
        writeStake(st.mode, unit, stakeText[unit]);
      }
    }
    const opts = verifyLiquidity ? { verifyLiquidity: true } : undefined;
    try {
      // confirm() is the strict second step, but it takes no options; with verify-liquidity on,
      // place(opts) again IS the confirm click (same inputs) and carries the option through.
      const out = viaConfirm ? (opts ? await sheet.place(opts) : await sheet.confirm()) : await sheet.place(opts);
      if (out.status === "placed") {
        for (const l of out.result.legs) for (const id of l.routerOrderIds) seenOrderIds.current.add(id);
        lookupRun.current++; // a late answer beats a watchdog lookup
        setLookup(null);
        // No sheet.refresh() after a decline: refresh() re-runs the sheet's load, which drops the
        // terminal phase (failed → ready) and wiped the "declined" screen before it was seen.
        // "Retry at …" uses the venue price the sheet already holds; the retry re-validates.
      } else if (snap) {
        setSend(null); // blocked / confirm_required: nothing was sent
      }
    } catch (e) {
      const error = toSheetError(e);
      if (snap && error.retryable) {
        startLookup(snap); // unknown outcome: it may have reached the venue
      } else {
        setSend(null);
        setPlaceError(error);
      }
    }
  };

  /** Back to an editable slip on the same position (the SDK's reset), keeping the stake. */
  const freshSlip = (o: { unit?: StakeUnit; stake?: string; maxPrice?: number | null } = {}) => {
    lookupRun.current++;
    setSend(null);
    setLookup(null);
    setPlaceError(null);
    setCancel({ busy: false, note: null });
    const u = o.unit ?? unit;
    const text = o.stake ?? stakeText[u];
    if (o.unit) setUnit(u);
    if (o.stake !== undefined) setStakeText((p) => ({ ...p, [u]: text }));
    if (o.maxPrice !== undefined) setPriceText(o.maxPrice == null ? null : centsText(o.maxPrice));
    sheet.reset();
    const n = parseStake(text);
    const max = o.maxPrice !== undefined ? o.maxPrice : priceNum;
    sheet.set({ stake: n == null ? null : u === "amount" ? { amount: n } : { shares: n }, ...(max != null ? { maxPrice: max } : {}) });
  };

  const runFix = (a: FixAction) => {
    switch (a.kind) {
      case "setStake": {
        const text = String(Math.round(a.value * 100) / 100);
        setUnit(a.unit);
        setStakeText((p) => ({ ...p, [a.unit]: text }));
        lastEditAt.current = 0;
        pushStake(a.unit, text);
        return;
      }
      case "setMaxPrice":
        setPriceText(centsText(a.value));
        lastEditAt.current = 0;
        sheet.set({ maxPrice: a.value });
        return;
      case "autoPrice":
        setPriceText(null);
        sheet.set({ maxPrice: null });
        return;
      case "select":
        setPlaceError(null);
        sheet.select(a.partnerId);
        return;
      case "unlock":
        showUnlock(SCOPES.trade);
        return;
      case "venueAccounts":
        window.open(VENUE_ACCOUNTS_URL, "_blank", "noopener,noreferrer");
        return;
      case "paper":
        setTradingMode("paper");
        return;
      case "real":
        if (canTrade) setTradingMode("real");
        else showUnlock(SCOPES.trade);
        return;
      case "market":
        sheet.set({ orderType: "market" });
        return;
      case "retry":
        lastQuietRefresh.current = Date.now();
        void sheet.refresh();
        return;
      case "openOptions":
        return; // handled in the view
    }
  };

  const close = () => onClose();
  const flyAndClose = (from: HTMLElement | null) => void flyToPositions(from).then(onClose);

  const actions: TicketActions = {
    onClose: close,
    onStakeText: (text) => {
      setStakeText((p) => ({ ...p, [unit]: text }));
      markEdit();
      pushStake(unit, text);
    },
    onChip: (value) => runFix({ kind: "setStake", unit, value }),
    onUnit: (u) => {
      setUnit(u);
      lastEditAt.current = 0;
      pushStake(u, stakeText[u]);
    },
    onPriceText: (text) => {
      setPriceText(text);
      markEdit();
      const p = parseCents(text);
      if (p != null) sheet.set({ maxPrice: p });
    },
    onFix: runFix,
    onOrderType: (t) => sheet.set({ orderType: t }),
    onVerify: setVerifyLiquidity,
    onPlace: () => void placeNow(false),
    onConfirm: () => void placeNow(true),
    onBack: () => sheet.cancelConfirm(),
    onPlaceAnother: () => freshSlip(),
    onDone: flyAndClose,
    onLeaveWorking: flyAndClose,
    onCancelResting: () => {
      // A REAL cancel at the venue — only ever from this explicit tap.
      setCancel({ busy: true, note: null });
      sheet
        .cancel()
        .then((r) => {
          setCancel({
            busy: false,
            note: r.cancelled.length > 0 ? null : r.skipped.length > 0 ? "It had already filled or closed — nothing to cancel." : "Nothing to cancel.",
          });
          invalidateAfterOrder(qc);
        })
        .catch((e) => setCancel({ busy: false, note: `Couldn't cancel: ${userMessageFor(toSheetError(e))}` }));
    },
    onRetry: (price) => {
      freshSlip({ maxPrice: price });
      void placeNow(false);
    },
    onTryRest: (amount, price) => {
      freshSlip({ unit: "amount", stake: String(amount), maxPrice: price });
      void placeNow(false);
    },
    onEdit: () => freshSlip(),
    onCheckAgain: () => send && startLookup(send),
  };

  // ── the view-model ──
  const ent = err ? entitlementOf(new ApiError(err.status ?? 0, err.code, err.message, err.details ?? {})) : null;
  const input: TicketInput = {
    sheet: h,
    everLoaded,
    oddsFormat: format,
    clicked: {
      title: position.title ?? "(position)",
      sideKey: position.side_key ?? null,
      marketLabel: position.market_label ?? null,
      venueName: pl.partner_name ?? pl.partner_id,
      venueLogoUrl: venueStatus.get(pl.partner_id)?.logoUrl ?? null,
      price: pl.price ?? null,
      americanOdds: pl.american_odds ?? null,
    },
    unit,
    stakeText: stakeText[unit],
    priceText,
    verifyLiquidity,
    canLimit,
    placeError: placeError ? { code: `${placeError.code}${placeError.status ? ` (${placeError.status})` : ""}`, message: userMessageFor(placeError) } : null,
    entitlementBlocked: !!ent,
    send,
    sendStep,
    lookup,
    cancel,
    declineReason,
    readinessOf: (id) => {
      const v = venueStatus.get(id);
      return v ? { connected: v.connected, balance: v.balance } : undefined;
    },
  };
  const vm = deriveTicketView(input, canTrade);
  const typing = Date.now() - lastEditAt.current < TYPING_PAUSE_MS;
  vm.problems = gateProblems(vm.problems, shownProblems.current, typing);
  shownProblems.current = vm.problems;

  return (
    <TicketView
      vm={vm}
      actions={actions}
      mobile={isMobile}
      unlockSlot={ent ? <UnlockCard feature="trading" entitlement={ent} compact /> : undefined}
      marketHref={marketLink && contestId ? marketHref({ contestId, positionHash: position.position_hash }) : undefined}
    />
  );
}
